import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, ArrowRight, GoogleLogo, DeviceMobile, EnvelopeSimple, CaretLeft, UsersThree, ChatCircleDots, CalendarStar, Graph } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SampleLinks from "@/components/SampleLinks";
import { SocialPulseButton } from "@/components/SocialPulseButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  firebaseReady, googleSignIn, sendPhoneOtp, confirmPhoneOtp, resetRecaptcha,
  sendEmailLink, isEmailLink, savedEmailForLink, stashPendingEmailLink, completeEmailLink,
  humanizeFirebaseError,
} from "@/lib/firebase";
import { COUNTRIES, toE164 } from "@/lib/phoneFormat";


function AuthShell({ children, title, subtitle, preTitle }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-primary text-primary-foreground lg:block grain">
        <img src="https://images.unsplash.com/photo-1758270704763-22072a90d3b6" alt="Students together in a lecture hall"
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/45 to-primary/15" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary-foreground text-primary"><GraduationCap weight="fill" size={20} /></span>
            <span className="font-heading text-lg font-semibold">AlumniPulse™</span>
          </Link>
          <div className="max-w-md">
            <p className="font-heading text-3xl font-light leading-snug">Your class. Your memories. <span className="italic text-secondary">Your community.</span></p>
            <p className="mt-4 text-sm text-primary-foreground/85">Bring your classmates, friends and fellow alumni together — a place to reconnect, communicate and stay involved for years to come.</p>
            <ul className="mt-6 space-y-3">
              {[[UsersThree, "Build your community", "Class, batch, school, campus or club — one home for everyone."],
                [ChatCircleDots, "Connect & communicate", "A persistent chat for messages, photos, announcements and memories."],
                [CalendarStar, "Organize together", "Create reunions and events with RSVPs and guest lists."],
                [Graph, "Grow your network", "Invite alumni and rediscover friends across generations."]].map(([Icon, t, d]) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary-foreground/15"><Icon size={17} weight="duotone" /></span>
                  <div><p className="text-sm font-medium">{t}</p><p className="text-xs text-primary-foreground/70">{d}</p></div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {preTitle}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-medium tracking-tight">{title}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function FirebaseMethods({ keep = true }) {
  const { firebaseLogin } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("choices"); // choices | phone | email
  const [busy, setBusy] = useState(false);
  // phone
  const [dial, setDial] = useState("1");
  const [phone, setPhone] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [otp, setOtp] = useState("");
  // email link
  const [linkEmail, setLinkEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  // safe-click confirm
  const [pendingLink, setPendingLink] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");

  useEffect(() => {
    if (firebaseReady && isEmailLink(window.location.href)) {
      stashPendingEmailLink(window.location.href);
      setPendingLink(true);
      setConfirmEmail(savedEmailForLink());
    }
  }, []);

  const finish = async (idToken, isNewMsg) => {
    const res = await firebaseLogin(idToken, keep);
    toast.success(res.is_new_user ? "Welcome to AlumniPulse!" : "Welcome back!");
    // Navigation is handled by the PublicOnly route guard, which honors ?next=.
  };

  const google = async () => {
    setBusy(true);
    try { finish(await googleSignIn()); }
    catch (err) { toast.error(humanizeFirebaseError(err)); }
    finally { setBusy(false); }
  };

  const sendOtp = async () => {
    if (!phone.trim()) return toast.error("Enter your phone number");
    setBusy(true);
    try {
      const conf = await sendPhoneOtp(toE164(dial, phone));
      setConfirmation(conf);
      toast.success("Code sent — check your messages");
    } catch (err) { resetRecaptcha(); toast.error(humanizeFirebaseError(err)); }
    finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    if (!otp.trim()) return toast.error("Enter the code");
    setBusy(true);
    try { finish(await confirmPhoneOtp(confirmation, otp)); }
    catch (err) { toast.error(humanizeFirebaseError(err)); }
    finally { setBusy(false); }
  };

  const sendLink = async () => {
    if (!linkEmail.trim()) return toast.error("Enter your email");
    setBusy(true);
    try { await sendEmailLink(linkEmail.trim()); setLinkSent(true); toast.success("Magic link sent — check your inbox"); }
    catch (err) { toast.error(humanizeFirebaseError(err)); }
    finally { setBusy(false); }
  };

  const confirmPending = async () => {
    if (!confirmEmail.trim()) return toast.error("Please enter the email you used");
    setBusy(true);
    try { finish(await completeEmailLink(confirmEmail.trim())); }
    catch (err) { toast.error(humanizeFirebaseError(err)); }
    finally { setBusy(false); }
  };

  if (!firebaseReady) return null;

  if (pendingLink) {
    return (
      <div className="mt-6 rounded-xl border border-secondary/30 bg-secondary/5 p-4" data-testid="email-link-confirm">
        <p className="text-sm font-medium">Finish signing in</p>
        <p className="mt-1 text-xs text-muted-foreground">Confirm your email to complete the magic-link sign-in.</p>
        <Input className="mt-3" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder="you@example.com" data-testid="email-link-confirm-input" />
        <Button className="mt-3 w-full rounded-full" onClick={confirmPending} disabled={busy} data-testid="email-link-confirm-btn">{busy ? "Signing in…" : "Confirm & sign in"}</Button>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="relative my-4 text-center"><span className="relative z-10 bg-background px-3 text-xs uppercase tracking-wide text-muted-foreground">or continue with</span><div className="absolute inset-x-0 top-1/2 h-px bg-border" /></div>

      {mode === "choices" && (
        <div className="space-y-2">
          <SocialPulseButton />
          <Button variant="outline" className="w-full rounded-full gap-2" onClick={google} disabled={busy} data-testid="google-signin"><GoogleLogo size={18} weight="bold" /> Continue with Google</Button>
          <Button variant="outline" className="w-full rounded-full gap-2" onClick={() => setMode("phone")} data-testid="phone-signin"><DeviceMobile size={18} /> Continue with phone</Button>
          <Button variant="outline" className="w-full rounded-full gap-2" onClick={() => setMode("email")} data-testid="emaillink-signin"><EnvelopeSimple size={18} /> Email me a magic link</Button>
        </div>
      )}

      {mode === "phone" && (
        <div className="space-y-3" data-testid="phone-flow">
          <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => { setMode("choices"); setConfirmation(null); }}><CaretLeft size={12} /> Back</button>
          {!confirmation ? (
            <>
              <div className="flex gap-2">
                <Select value={dial} onValueChange={setDial}>
                  <SelectTrigger className="w-28" data-testid="phone-country"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.dial}>{c.code} +{c.dial}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" type="tel" data-testid="phone-number" />
              </div>
              <p className="text-xs text-muted-foreground" data-testid="phone-sms-note">
                <ChatCircleDots size={13} className="mr-1 inline align-[-2px]" /> We'll send a 6-digit code by <span className="font-medium text-foreground">SMS</span> to this number. Standard rates may apply.
              </p>
              <Button className="w-full rounded-full" onClick={sendOtp} disabled={busy} data-testid="phone-send-otp">{busy ? "Sending…" : "Send code"}</Button>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground" data-testid="phone-otp-note">Enter the 6-digit code we just texted you.</p>
              <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" inputMode="numeric" data-testid="phone-otp" />
              <Button className="w-full rounded-full" onClick={verifyOtp} disabled={busy} data-testid="phone-verify-otp">{busy ? "Verifying…" : "Verify & sign in"}</Button>
            </>
          )}
        </div>
      )}

      {mode === "email" && (
        <div className="space-y-3" data-testid="email-flow">
          <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => { setMode("choices"); setLinkSent(false); }}><CaretLeft size={12} /> Back</button>
          {linkSent ? (
            <p className="rounded-lg bg-muted/60 p-3 text-center text-sm text-muted-foreground" data-testid="email-link-sent">We've emailed a sign-in link to <strong>{linkEmail}</strong>. Open it on this device to continue.</p>
          ) : (
            <>
              <Input value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="you@example.com" type="email" data-testid="emaillink-email" />
              <Button className="w-full rounded-full" onClick={sendLink} disabled={busy} data-testid="emaillink-send">{busy ? "Sending…" : "Send magic link"}</Button>
            </>
          )}
        </div>
      )}

      <div id="recaptcha-container" />
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keep, setKeep] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, keep);
      toast.success("Welcome back!");
      // Navigation handled by the PublicOnly route guard (honors ?next=).
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Sign in to AlumniPulse™" subtitle="Powered by SocialPulse® — one account across the network." preTitle={<SampleLinks className="mb-6" />}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="you@example.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" />
        </div>
        <label className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Checkbox checked={keep} onCheckedChange={(v) => setKeep(!!v)} data-testid="keep-signed-in" /> Keep me signed in <span className="text-xs">(12h idle limit)</span>
        </label>
        <Button type="submit" className="w-full rounded-full gap-2" disabled={loading} data-testid="login-submit">
          {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
        </Button>
        <p className="rounded-lg bg-muted/60 p-3 text-center text-xs text-muted-foreground">
          Demo: demo@alumnipulse.net · Demo@12345
        </p>
        <p className="text-center text-sm text-muted-foreground">
          New here? Signing in with a method below also creates your account — or <Link to="/register" className="font-medium text-secondary hover:underline">register with your details</Link>.
        </p>
      </form>
      <FirebaseMethods keep={keep} />
    </AuthShell>
  );
}

export { AuthShell };
